import { useMemo } from "react";

import { FEATURE_FLAGS } from "@/lib/feature-flags/flags";

import { toIsoDate } from "@/dashboard/components/date/DateRangePicker";
import { useAuth } from "@/dashboard/contexts/auth-context";
import { useFeatureFlags } from "@/dashboard/contexts/feature-context";
import { useCustomerListRefreshBudget } from "@/dashboard/features/hub/hooks/customer/use-customer-list-refresh-budget";
import { useCurrentHubTab } from "@/dashboard/features/hub/hooks/useCurrentHubTab";
import { HUB_POLL_INTERVALS_MS } from "@/dashboard/features/hub/lib/constants";
import { CustomerPageResponse, CustomerRow } from "@/dashboard/features/hub/lib/customer/schemas";
import { formatBusinessUnitSelection } from "@/dashboard/features/hub/lib/filters/business-unit-selection";
import { filtersKey, HubFilters } from "@/dashboard/features/hub/lib/filters/hub-filters";
import { CustomerListWindowClient } from "@/dashboard/features/hub/realtime/customer-list-window-client";
import { useCustomerListPollInterval } from "@/dashboard/features/hub/realtime/hub-realtime-status";
import { useCustomerListWindow } from "@/dashboard/features/hub/realtime/use-customer-list-window";
import { fetchJson } from "@/dashboard/hooks/api/fetch-json";
import { retryTemporaryBusyOnce } from "@/dashboard/hooks/query/retry-policy";
import { useInfiniteCursorQuery } from "@/dashboard/hooks/query/use-infinite-cursor-query";
import { tenantKey } from "@/dashboard/hooks/query/use-tenant-query";
import { HubTab } from "@/dashboard/types/hub";

const BASE_PATH = "/api/dashboard/customers";
export const CUSTOMERS_QUERY_KEY_PREFIX = "customers" as const;
export const CUSTOMERS_QUERY_PAGE_SIZE = 20;

/** Serializes HubFilters + search into the query string shape the list endpoints expect. */
export function buildCustomerListQueryParams(
  filters: HubFilters,
  search?: string
): URLSearchParams {
  const qs = new URLSearchParams();

  const trimmed = search?.trim();
  if (trimmed) {
    qs.set("search", trimmed);
  }

  if (filters.assignment) {
    qs.set("assignment", filters.assignment);
  }

  for (const m of filters.modalities ?? []) {
    qs.append("modality", m);
  }

  for (const c of filters.channelTypes ?? []) {
    qs.append("channel_type", c);
  }

  for (const badge of filters.badges ?? []) {
    qs.append("badge", badge);
  }

  for (const campaign of filters.campaigns ?? []) {
    qs.append("campaign", campaign);
  }

  for (const businessUnit of filters.businessUnits ?? []) {
    qs.append("business_unit_id", formatBusinessUnitSelection(businessUnit));
  }

  for (const userId of filters.assignedToUserIds ?? []) {
    qs.append("assigned_to_user_id", userId);
  }

  for (const userGroupId of filters.assignedToUserGroupIds ?? []) {
    qs.append("assigned_to_user_group_id", userGroupId);
  }

  for (const status of filters.audienceMemberStatus ?? []) {
    qs.append("audience_member_status", status);
  }

  for (const digitalLeadSource of filters.digitalLeadSources ?? []) {
    qs.append("digital_lead_source", digitalLeadSource);
  }

  for (const homeClub of filters.homeClubs ?? []) {
    qs.append("home_club", homeClub);
  }

  for (const contactType of filters.contactTypes ?? []) {
    qs.append("contact_type", contactType);
  }

  for (const department of filters.transferDepartments ?? []) {
    qs.append("transfer_department", department);
  }

  if (filters.start) {
    qs.set("date_from", toIsoDate(filters.start));
  }
  if (filters.end) {
    qs.set("date_to", toIsoDate(filters.end));
  }

  if (filters.hasUnrespondedText) {
    qs.set("unrespondedInboundText", "true");
  }

  if (filters.hasResponse) {
    qs.set("hasResponse", "true");
  }

  // A selection persisted before folders became name-based carries no folderName.
  if (filters.folder?.folderName) {
    qs.set("folder_name", filters.folder.folderName);
    for (const tenantId of filters.folder.tenantIds ?? []) {
      qs.append("folder_tenant_ids", tenantId);
    }
  }

  return qs;
}

/* helpers */
export function getCustomersPage({
  filters,
  hubTab,
  limit,
  cursor,
  search,
  signal,
}: {
  filters: HubFilters;
  hubTab: HubTab;
  limit: number;
  cursor?: string;
  search?: string;
  signal?: AbortSignal;
}) {
  const qs = buildCustomerListQueryParams(filters, search);
  qs.set("limit", String(limit));
  if (cursor) {
    qs.set("cursor", cursor);
  }

  const path = hubTab === HubTab.ALL ? BASE_PATH : `${BASE_PATH}/${hubTab}`;
  return fetchJson<CustomerPageResponse>(`${path}?${qs}`, { signal });
}

export function customersListQueryKey(filters: HubFilters, hubTab: HubTab, search?: string) {
  /**
   * IMPORTANT: do not change the order of this tuple.
   * Specifically, there are places where we assume the hubTab comes after the
   * customers prefix.
   */
  return [CUSTOMERS_QUERY_KEY_PREFIX, hubTab, filtersKey(filters), search] as const;
}

export function useCustomersInfiniteQuery(filters: HubFilters, search?: string) {
  const hubTab = useCurrentHubTab();
  const { activeTenantId, user } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const trimmed = search?.trim() || undefined;
  const refreshBudgetEnabled = hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_REFRESH_BUDGET);
  const fallbackRefetchInterval = useCustomerListPollInterval(HUB_POLL_INTERVALS_MS.customersList);
  const customerListKeyPrefix = useMemo(
    () => tenantKey(activeTenantId ?? "", CUSTOMERS_QUERY_KEY_PREFIX),
    [activeTenantId]
  );
  const refreshBudget = useCustomerListRefreshBudget({
    enabled: refreshBudgetEnabled,
    queryKey: customerListKeyPrefix,
    schedule: true,
    intervalMs: fallbackRefetchInterval,
  });
  const refetchInterval = refreshBudgetEnabled ? false : fallbackRefetchInterval;
  const listKey = customersListQueryKey(filters, hubTab, trimmed);
  const windowTab = hubTab === HubTab.ALL || hubTab === HubTab.TRANSFERRED ? hubTab : null;
  useCustomerListWindow({
    enabled:
      hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_DELTAS) &&
      hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_PROJECTION) &&
      hasFeature(FEATURE_FLAGS.HUB_CUSTOMER_LIST_REALTIME) &&
      refreshBudgetEnabled &&
      CustomerListWindowClient.supportsView(
        filters,
        trimmed,
        hubTab,
        user?.neticRole === "call_taker"
      ),
    queryKey: tenantKey(activeTenantId ?? "", ...listKey),
    tab: windowTab,
    atTop: refreshBudget.atTop,
  });

  return useInfiniteCursorQuery<CustomerRow>({
    queryKey: listKey,
    tenantScoped: true,
    fetchPage: (cursor, signal) =>
      getCustomersPage({
        filters,
        hubTab,
        limit: CUSTOMERS_QUERY_PAGE_SIZE,
        cursor: cursor ?? undefined,
        search: trimmed,
        signal,
      }),
    staleTime: 60_000,
    // Retry a temporary pool-acquire failure once; don't repeat a 15-second
    // query read timeout or another server error under load.
    retry: retryTemporaryBusyOnce,
    retryDelay: 2_000,
    // The coordinator owns automatic refreshes while the budget flag is enabled. A remount
    // refetch would replay every stale cursor page without trimming. Otherwise keep the ordinary
    // polling interval or the list capability's five-minute safety net.
    refetchInterval,
    refetchOnWindowFocus: !refreshBudgetEnabled,
    refetchOnReconnect: !refreshBudgetEnabled,
    refetchOnMount: !refreshBudgetEnabled,
  });
}
